import type { ReactNode } from 'react'

/**
 * Blocul cu care incepe fiecare ecran din ADM - `_pagehead.php`.
 *
 * Macheta primea `pageHeader` de la fiecare controller si nu-l randa nicaieri,
 * asa ca ecranele astea incepeau cu o bara de filtre si un tabel si nu spuneau
 * niciodata ce sunt: cine venea dintr-o legatura trebuia sa ghiceasca ecranul
 * dupa capetele de coloana.
 *
 * Deci ecranul o spune singur: o iconita in culoarea sectiunii, numele, un rand
 * despre ce e pe el si - in dreapta - cifrele care hotarasc daca e ceva de
 * facut aici.
 *
 * `tone` e acelasi vocabular ca la cartonasele platformei: rosu e defectiune,
 * galben e "asteapta un om", verde e crestere, mov e masurat fata de o tinta
 * contractata, albastru e "se intampla acum".
 *
 * Clasele sunt cele din macheta (`adm-stat`, `adm-page-note`, ...), fiindca
 * foaia e tot cea a portalului: `public/css/adm.css`.
 */
export interface AdmStat {
  value: string
  label: string
  tone: string
  /** Pentru cifrele care se schimba fara ca pagina sa fie desenata din nou. */
  id?: string
}

export function AdmPageHead({
  tone,
  icon,
  title,
  note,
  stats = [],
  actions,
  titleExtra,
}: {
  tone: string
  icon: string
  title: string
  note: string
  stats?: AdmStat[]
  /** Ce poate face pagina, pe langa numele ei: cifrele spun ce e, butonul ce faci. */
  actions?: ReactNode
  /**
   * Un control pe ACEEASI linie cu numele ecranului.
   *
   * `actions` sta dupa cifre, si acolo ii e locul unui buton. Dar un control
   * care schimba INTELESUL cifrelor - filele, fereastra de timp - citit dupa
   * ele ajunge prea tarziu. El apartine titlului, fiindca il completeaza.
   */
  titleExtra?: ReactNode
}) {
  return (
    <div className={`adm-page-head adm-tone-${tone}`}>
      <span className="adm-page-icon">
        <i className={`fas ${icon}`}></i>
      </span>

      <div className="adm-page-title">
        <div className="adm-page-title-line">
          <h3>{title}</h3>
          {titleExtra ? <span className="adm-page-title-extra">{titleExtra}</span> : null}
        </div>
        <span className="adm-page-note">{note}</span>
      </div>

      {stats.length > 0 ? (
        <div className="adm-page-stats">
          {stats.map((stat) => (
            <div key={stat.label} className={`adm-stat adm-stat-${stat.tone}`}>
              <span className="adm-stat-value" id={stat.id}>
                {stat.value}
              </span>
              <span className="adm-stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      ) : null}

      {actions ? <div className="adm-page-actions">{actions}</div> : null}
    </div>
  )
}
